<template>
  <transition name="fade">
    <div class="ai-chat-container" v-if="show">
      <div class="ai-chat-window">
        <!-- Header -->
        <div class="ai-chat-header">
          <div class="d-flex align-items-center">
            <div class="ai-avatar me-2">
              <i class="fas fa-robot"></i>
            </div>
            <div>
              <h6 class="mb-0 text-white">{{ $t('aiAssistant') || 'Assistente IA' }}</h6>
              <small class="text-white-50">{{ isCoach ? 'Coach Mode' : 'Student Mode' }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <button
              v-if="conversations.length > 0"
              class="btn btn-link text-white p-1 me-2"
              @click="showHistory = !showHistory"
              :title="$t('conversationHistory') || 'Histórico'"
            >
              <i class="fas fa-history"></i>
            </button>
            <button class="btn btn-link text-white p-1 me-2" @click="startNewConversation" :title="$t('newConversation') || 'Nova conversa'">
              <i class="fas fa-plus"></i>
            </button>
            <button class="btn btn-link text-white p-1" @click="closeChat">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Conversation History Sidebar -->
        <transition name="slide">
          <div class="ai-history-sidebar" v-if="showHistory">
            <div class="p-2 border-bottom">
              <small class="text-muted fw-bold">{{ $t('conversations') || 'Conversas' }}</small>
            </div>
            <div class="ai-history-list">
              <div
                v-for="conv in conversations"
                :key="conv.id"
                class="ai-history-item"
                :class="{ active: currentConversationId === conv.id }"
                @click="loadConversation(conv.id)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <span class="text-truncate">{{ conv.title }}</span>
                  <button class="btn btn-link text-danger p-0 ms-2" @click.stop="deleteConversation(conv.id)">
                    <i class="fas fa-trash-alt fa-xs"></i>
                  </button>
                </div>
                <small class="text-muted">{{ formatDate(conv.last_message_at) }}</small>
              </div>
            </div>
          </div>
        </transition>

        <!-- Training Generation Panel (Coach only) -->
        <div v-if="isCoach && showTrainingPanel" class="ai-training-panel">
          <div class="p-3 border-bottom">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0"><i class="fas fa-dumbbell me-2"></i>{{ $t('generateTraining') || 'Gerar Treino' }}</h6>
              <button class="btn btn-link text-muted p-0" @click="showTrainingPanel = false">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm">{{ $t('selectStudent') || 'Selecionar Aluno' }}</label>
              <select v-model="trainingForm.student_id" class="form-select form-select-sm">
                <option value="">{{ $t('selectStudent') || 'Selecione um aluno' }}</option>
                <option v-for="student in students" :key="student.id" :value="student.id">
                  {{ student.name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm">{{ $t('objective') || 'Objetivo' }}</label>
              <select v-model="trainingForm.objective" class="form-select form-select-sm">
                <option value="Hipertrofia">{{ $t('hypertrophy') || 'Hipertrofia' }}</option>
                <option value="Força">{{ $t('strength') || 'Força' }}</option>
                <option value="Resistência">{{ $t('endurance') || 'Resistência' }}</option>
                <option value="Emagrecimento">{{ $t('weightLoss') || 'Emagrecimento' }}</option>
                <option value="Condicionamento">{{ $t('conditioning') || 'Condicionamento' }}</option>
              </select>
            </div>

            <div class="row">
              <div class="col-6 mb-3">
                <label class="form-label text-sm">{{ $t('daysPerWeek') || 'Dias/Semana' }}</label>
                <select v-model="trainingForm.days_per_week" class="form-select form-select-sm">
                  <option v-for="n in 7" :key="n" :value="n">{{ n }}</option>
                </select>
              </div>
              <div class="col-6 mb-3">
                <label class="form-label text-sm">{{ $t('weeks') || 'Semanas' }}</label>
                <select v-model="trainingForm.duration_weeks" class="form-select form-select-sm">
                  <option v-for="n in 12" :key="n" :value="n">{{ n }}</option>
                </select>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm">{{ $t('level') || 'Nível' }}</label>
              <select v-model="trainingForm.level" class="form-select form-select-sm">
                <option value="Iniciante">{{ $t('beginner') || 'Iniciante' }}</option>
                <option value="Intermediário">{{ $t('intermediate') || 'Intermediário' }}</option>
                <option value="Avançado">{{ $t('advanced') || 'Avançado' }}</option>
              </select>
            </div>

            <!-- Configuração de Zonas de Carga -->
            <div class="mb-3">
              <div class="d-flex align-items-center mb-2">
                <label class="form-label text-sm mb-0 me-2">Zonas de Carga (%)</label>
                <div class="form-check form-switch ms-auto">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="enableLoadZones"
                    v-model="trainingForm.enable_load_zones"
                  >
                  <label class="form-check-label text-xs" for="enableLoadZones">Configurar</label>
                </div>
              </div>

              <div v-if="trainingForm.enable_load_zones" class="load-zones-config p-2 bg-light rounded">
                <div class="row mb-2">
                  <div class="col-12">
                    <label class="form-label text-xs text-muted mb-1">Quantas cargas (séries) por exercício?</label>
                    <select v-model="trainingForm.num_load_zones" class="form-select form-select-sm">
                      <option :value="1">1 carga</option>
                      <option :value="2">2 cargas</option>
                      <option :value="3">3 cargas</option>
                      <option :value="4">4 cargas</option>
                      <option :value="5">5 cargas</option>
                    </select>
                  </div>
                </div>

                <div class="row mb-2">
                  <div class="col-6">
                    <label class="form-label text-xs text-muted mb-1">% Inicial</label>
                    <select v-model="trainingForm.load_percent_start" class="form-select form-select-sm">
                      <option v-for="p in percentOptions" :key="p" :value="p">{{ p }}%</option>
                    </select>
                  </div>
                  <div class="col-6">
                    <label class="form-label text-xs text-muted mb-1">% Final</label>
                    <select v-model="trainingForm.load_percent_end" class="form-select form-select-sm">
                      <option v-for="p in percentOptions" :key="p" :value="p">{{ p }}%</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <label class="form-label text-xs text-muted mb-1">Intervalo entre percentuais</label>
                    <select v-model="trainingForm.load_percent_interval" class="form-select form-select-sm">
                      <option :value="5">5%</option>
                      <option :value="10">10%</option>
                      <option :value="15">15%</option>
                      <option :value="20">20%</option>
                    </select>
                  </div>
                </div>

                <!-- Preview das zonas -->
                <div v-if="loadZonesPreview.length > 0" class="mt-2 p-2 bg-white rounded border">
                  <small class="text-muted d-block mb-1">Preview das zonas:</small>
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      v-for="(zone, idx) in loadZonesPreview"
                      :key="idx"
                      class="badge bg-gradient-primary"
                    >
                      {{ zone }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm">{{ $t('notes') || 'Observações' }}</label>
              <textarea
                v-model="trainingForm.notes"
                class="form-control form-control-sm"
                rows="2"
                :placeholder="$t('additionalNotes') || 'Observações adicionais...'"
              ></textarea>
            </div>

            <button
              class="btn bg-gradient-primary w-100"
              @click="generateTraining"
              :disabled="!trainingForm.student_id || isGenerating"
            >
              <span v-if="isGenerating">
                <i class="fas fa-spinner fa-spin me-2"></i>{{ $t('generating') || 'Gerando...' }}
              </span>
              <span v-else>
                <i class="fas fa-magic me-2"></i>{{ $t('generatePlan') || 'Gerar Plano' }}
              </span>
            </button>
          </div>
        </div>

        <!-- Generated Training Preview Panel (full screen like training form) -->
        <div v-if="generatedTraining" class="ai-training-panel">
          <div class="p-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0"><i class="fas fa-clipboard-list me-2"></i>{{ generatedTraining.name }}</h6>
              <button class="btn btn-link text-muted p-0" @click="generatedTraining = null">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="training-info mb-3">
              <span class="badge bg-gradient-info me-2">{{ generatedTraining.objective }}</span>
              <span class="badge bg-gradient-secondary">{{ generatedTraining.student_name }}</span>
            </div>

            <div class="training-weeks-preview">
              <!-- Mostra a estrutura semanal (que será replicada) -->
              <div class="week-item mb-2">
                <div class="fw-bold text-sm mb-1">
                  <i class="fas fa-calendar-week me-1"></i>
                  {{ $t('weeklyStructure') || 'Estrutura Semanal' }}
                  <span class="badge bg-gradient-primary ms-2">{{ getWeeksCount() }} {{ $t('weeks') || 'semanas' }}</span>
                </div>
                <div v-if="generatedTraining.weeks && generatedTraining.weeks[0]">
                  <div v-for="(day, dIndex) in generatedTraining.weeks[0].days" :key="dIndex" class="day-item ps-2">
                    <small class="text-muted">
                      <i class="fas fa-dumbbell me-1"></i>
                      {{ translateDay(day.day_name) }}: <strong>{{ day.focus }}</strong>
                    </small>
                    <div class="text-xs text-muted ps-3">
                      {{ day.exercises.length }} {{ $t('exercises') || 'exercícios' }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-xs text-muted mt-2">
                <i class="fas fa-info-circle me-1"></i>
                {{ $t('trainingReplicatedInfo') || 'Este treino será replicado em todas as semanas do plano.' }}
              </div>
            </div>

            <div class="d-flex gap-2 mt-3">
              <button class="btn bg-gradient-success flex-grow-1" @click="saveTraining" :disabled="isSaving">
                <span v-if="isSaving">
                  <i class="fas fa-spinner fa-spin me-2"></i>{{ $t('saving') || 'Salvando...' }}
                </span>
                <span v-else>
                  <i class="fas fa-save me-2"></i>{{ $t('saveTraining') || 'Salvar Treino' }}
                </span>
              </button>
              <button class="btn bg-gradient-secondary" @click="generatedTraining = null" title="Descartar">
                <i class="fas fa-redo"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Messages Area -->
        <div class="ai-chat-messages" ref="messagesContainer" v-show="!showTrainingPanel && !generatedTraining">
          <div v-if="messages.length === 0" class="ai-welcome-message">
            <div class="ai-avatar-large mb-3">
              <i class="fas fa-robot"></i>
            </div>
            <h5>{{ $t('welcomeAI') || 'Olá! Como posso ajudar?' }}</h5>
            <p class="text-muted mb-3">
              {{ isCoach
                ? ($t('coachWelcome') || 'Posso ajudar com dúvidas técnicas sobre treinos, exercícios e periodização.')
                : ($t('studentWelcome') || 'Posso ajudar com dúvidas sobre seus treinos e execução dos exercícios.')
              }}
            </p>

            <!-- Quick Actions for Coach -->
            <div v-if="isCoach" class="quick-actions">
              <button class="btn btn-sm bg-gradient-info mb-2 me-2" @click="openTrainingPanel">
                <i class="fas fa-dumbbell me-1"></i>{{ $t('generateTraining') || 'Gerar Treino' }}
              </button>
            </div>

            <!-- Suggestion chips -->
            <div class="suggestion-chips">
              <button
                v-for="(suggestion, index) in suggestions"
                :key="index"
                class="suggestion-chip"
                @click="sendSuggestion(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="index"
            class="ai-message"
            :class="message.role"
          >
            <div class="message-avatar" v-if="message.role === 'assistant'">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="message-text" v-html="formatMessage(message.content)"></div>
              <small class="message-time">{{ formatTime(message.created_at) }}</small>
            </div>
            <div class="message-avatar" v-if="message.role === 'user'">
              <i class="fas fa-user"></i>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isTyping" class="ai-message assistant">
            <div class="message-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Generating training indicator -->
          <div v-if="isGenerating" class="ai-message assistant">
            <div class="message-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="generating-indicator">
                <i class="fas fa-dumbbell fa-spin me-2"></i>
                <span>{{ $t('generatingTraining') || 'Gerando plano de treino...' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="ai-chat-input" v-show="!showTrainingPanel && !generatedTraining">
          <div class="input-wrapper">
            <textarea
              v-model="inputMessage"
              :placeholder="$t('typeMessage') || 'Digite sua mensagem...'"
              @keydown.enter.exact.prevent="sendMessage"
              rows="1"
              ref="inputField"
            ></textarea>
            <button
              class="send-btn"
              @click="sendMessage"
              :disabled="!inputMessage.trim() || isTyping"
            >
              <i class="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch, inject } from 'vue'
import { useStore } from 'vuex'
import aiAssistantRepository from '@/services/api/aiAssistantRepository'

export default {
  name: 'AIChat',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore()
    const swal = inject('$swal')

    // State
    const messages = ref([])
    const inputMessage = ref('')
    const isTyping = ref(false)
    const conversations = ref([])
    const currentConversationId = ref(null)
    const showHistory = ref(false)
    const messagesContainer = ref(null)
    const inputField = ref(null)

    // Training generation state
    const showTrainingPanel = ref(false)
    const students = ref([])
    const isGenerating = ref(false)
    const isSaving = ref(false)
    const generatedTraining = ref(null)
    const trainingForm = ref({
      student_id: '',
      objective: 'Hipertrofia',
      days_per_week: 4,
      duration_weeks: 4,
      level: 'Intermediário',
      notes: '',
      // Configuração de zonas de carga
      enable_load_zones: false,
      num_load_zones: 3,
      load_percent_start: 60,
      load_percent_end: 80,
      load_percent_interval: 10
    })

    // Opções de percentuais (40% a 100%)
    const percentOptions = computed(() => {
      const options = []
      for (let i = 40; i <= 100; i += 5) {
        options.push(i)
      }
      return options
    })

    // Preview das zonas de carga calculadas
    const loadZonesPreview = computed(() => {
      if (!trainingForm.value.enable_load_zones) return []

      const start = trainingForm.value.load_percent_start
      const end = trainingForm.value.load_percent_end
      const interval = trainingForm.value.load_percent_interval
      const numZones = trainingForm.value.num_load_zones

      const zones = []

      if (start === end) {
        // Mesma porcentagem para todas as cargas
        for (let i = 0; i < numZones; i++) {
          zones.push(start)
        }
      } else if (start < end) {
        // Progressão crescente
        const step = (end - start) / (numZones - 1 || 1)
        for (let i = 0; i < numZones; i++) {
          zones.push(Math.round(start + (step * i)))
        }
      } else {
        // Progressão decrescente
        const step = (start - end) / (numZones - 1 || 1)
        for (let i = 0; i < numZones; i++) {
          zones.push(Math.round(start - (step * i)))
        }
      }

      return zones
    })

    // Computed
    // UserType is 'c' for coach, 's' for student, 'a' for admin
    const isCoach = computed(() => store.getters.UserType === 'c' || store.getters.UserType === 'a')

    const suggestions = computed(() => {
      if (isCoach.value) {
        return [
          'Como montar um treino de hipertrofia?',
          'Quais exercícios para iniciantes?',
          'Explique o método drop-set',
          'Periodização para força'
        ]
      }
      return [
        'Como executar o supino corretamente?',
        'Qual é meu treino de hoje?',
        'Dicas para agachamento',
        'Quanto tempo de descanso?'
      ]
    })

    // Methods
    const closeChat = () => {
      emit('close')
    }

    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }

    const formatMessage = (content) => {
      if (!content) return ''
      // Simple markdown-like formatting without external library
      let formatted = content
        // Escape HTML
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Line breaks
        .replace(/\n/g, '<br>')
        // Code blocks
        .replace(/`(.*?)`/g, '<code>$1</code>')
      return formatted
    }

    const formatTime = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    }

    const translateDay = (dayName) => {
      const days = {
        sunday: 'Domingo',
        monday: 'Segunda',
        tuesday: 'Terça',
        wednesday: 'Quarta',
        thursday: 'Quinta',
        friday: 'Sexta',
        saturday: 'Sábado'
      }
      return days[dayName] || dayName
    }

    const getWeeksCount = () => {
      if (!generatedTraining.value) return 0
      const initialDate = new Date(generatedTraining.value.initial_date)
      const finalDate = new Date(generatedTraining.value.final_date)
      const diffTime = Math.abs(finalDate - initialDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.ceil(diffDays / 7)
    }

    const sendMessage = async () => {
      if (!inputMessage.value.trim() || isTyping.value) return

      const userMessage = inputMessage.value.trim()
      inputMessage.value = ''

      // Add user message to UI
      messages.value.push({
        role: 'user',
        content: userMessage,
        created_at: new Date().toISOString()
      })

      scrollToBottom()
      isTyping.value = true

      try {
        console.log('Sending message to API:', {
          conversation_id: currentConversationId.value,
          message: userMessage
        })

        const response = await aiAssistantRepository.sendMessage({
          conversation_id: currentConversationId.value,
          message: userMessage
        })

        console.log('API Response:', response)

        if (response.data && response.data.success) {
          currentConversationId.value = response.data.data.conversation_id
          console.log('Conversation ID:', currentConversationId.value)

          messages.value.push({
            role: 'assistant',
            content: response.data.data.reply,
            created_at: new Date().toISOString()
          })

          // Refresh conversations list
          loadConversations()
        } else {
          console.error('API returned error:', response.data)
          messages.value.push({
            role: 'assistant',
            content: response.data?.message || 'Desculpe, ocorreu um erro. Tente novamente.',
            created_at: new Date().toISOString()
          })
        }
      } catch (error) {
        console.error('Error sending message:', error)
        console.error('Error response:', error.response?.data)

        let errorMessage = 'Desculpe, ocorreu um erro. Tente novamente.'
        if (error.response?.status === 429) {
          errorMessage = 'Limite diário de mensagens atingido. Tente novamente amanhã.'
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }

        messages.value.push({
          role: 'assistant',
          content: errorMessage,
          created_at: new Date().toISOString()
        })
      } finally {
        isTyping.value = false
        scrollToBottom()
      }
    }

    const sendSuggestion = (suggestion) => {
      inputMessage.value = suggestion
      sendMessage()
    }

    const loadConversations = async () => {
      try {
        console.log('Loading conversations...')
        const response = await aiAssistantRepository.getConversations()
        console.log('Conversations response:', response)
        if (response.data && response.data.success) {
          conversations.value = response.data.data || []
          console.log('Conversations loaded:', conversations.value.length)
        } else {
          console.warn('Failed to load conversations:', response.data)
        }
      } catch (error) {
        console.error('Error loading conversations:', error)
        // Don't show error to user, just log it
      }
    }

    const loadConversation = async (id) => {
      try {
        store.commit('SET_SPLASH_SCREEN', true)
        const response = await aiAssistantRepository.getConversation(id)
        if (response.data.success) {
          currentConversationId.value = id
          messages.value = response.data.data.messages || []
          showHistory.value = false
          scrollToBottom()
        }
      } catch (error) {
        console.error('Error loading conversation:', error)
      } finally {
        store.commit('SET_SPLASH_SCREEN', false)
      }
    }

    const deleteConversation = async (id) => {
      const result = await swal.fire({
        title: 'Excluir conversa?',
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar'
      })

      if (result.isConfirmed) {
        try {
          await aiAssistantRepository.deleteConversation(id)
          conversations.value = conversations.value.filter(c => c.id !== id)

          if (currentConversationId.value === id) {
            startNewConversation()
          }
        } catch (error) {
          console.error('Error deleting conversation:', error)
        }
      }
    }

    const startNewConversation = () => {
      currentConversationId.value = null
      messages.value = []
      showHistory.value = false
      generatedTraining.value = null
    }

    // Training generation methods
    const openTrainingPanel = async () => {
      showTrainingPanel.value = true
      if (students.value.length === 0) {
        await loadStudents()
      }
    }

    const loadStudents = async () => {
      try {
        const response = await aiAssistantRepository.getStudents()
        if (response.data.success) {
          students.value = response.data.data
        }
      } catch (error) {
        console.error('Error loading students:', error)
      }
    }

    const generateTraining = async () => {
      if (!trainingForm.value.student_id) return

      isGenerating.value = true
      showTrainingPanel.value = false

      // Build user message with load zones info
      const selectedStudent = students.value.find(s => s.id === trainingForm.value.student_id)
      let userMessageContent = `Gerar treino para ${selectedStudent?.name || 'aluno'}:\n- Objetivo: ${trainingForm.value.objective}\n- ${trainingForm.value.days_per_week} dias/semana\n- ${trainingForm.value.duration_weeks} semanas\n- Nível: ${trainingForm.value.level}`

      // Add load zones info if enabled
      if (trainingForm.value.enable_load_zones && loadZonesPreview.value.length > 0) {
        userMessageContent += `\n- Zonas de carga: ${loadZonesPreview.value.map(z => z + '%').join(' → ')}`
      }

      if (trainingForm.value.notes) {
        userMessageContent += '\n- Obs: ' + trainingForm.value.notes
      }

      // Add message to chat UI
      messages.value.push({
        role: 'user',
        content: userMessageContent,
        created_at: new Date().toISOString()
      })
      scrollToBottom()

      try {
        // First, save the conversation via chat endpoint
        const chatResponse = await aiAssistantRepository.sendMessage({
          conversation_id: currentConversationId.value,
          message: userMessageContent
        })

        if (chatResponse.data && chatResponse.data.success) {
          currentConversationId.value = chatResponse.data.data.conversation_id
        }

        // Then generate the training plan
        const response = await aiAssistantRepository.generateTraining(trainingForm.value)

        if (response.data.success) {
          generatedTraining.value = response.data.data

          const assistantMessage = `Plano de treino gerado com sucesso!\n\n**${response.data.data.name}**\n\nObjetivo: ${response.data.data.objective}\n\nO plano inclui ${response.data.data.weeks?.length || 0} semana(s) de treino. Você pode visualizar e salvar o plano acima.`

          messages.value.push({
            role: 'assistant',
            content: assistantMessage,
            created_at: new Date().toISOString()
          })

          // Refresh conversations list
          loadConversations()
        }
      } catch (error) {
        console.error('Error generating training:', error)
        messages.value.push({
          role: 'assistant',
          content: 'Desculpe, não foi possível gerar o plano de treino. Tente novamente.',
          created_at: new Date().toISOString()
        })
      } finally {
        isGenerating.value = false
        scrollToBottom()
      }
    }

    const saveTraining = async () => {
      if (!generatedTraining.value) return

      isSaving.value = true

      try {
        const response = await aiAssistantRepository.saveTraining({
          student_id: generatedTraining.value.student_id,
          training_plan: generatedTraining.value
        })

        if (response.data.success) {
          swal.fire({
            title: 'Sucesso!',
            text: 'Treino salvo com sucesso!',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          })

          messages.value.push({
            role: 'assistant',
            content: `Treino "${response.data.data.name}" salvo com sucesso! Você pode visualizá-lo na área de planos de treino.`,
            created_at: new Date().toISOString()
          })

          generatedTraining.value = null
        }
      } catch (error) {
        console.error('Error saving training:', error)
        swal.fire({
          title: 'Erro',
          text: 'Não foi possível salvar o treino. Tente novamente.',
          icon: 'error'
        })
      } finally {
        isSaving.value = false
        scrollToBottom()
      }
    }

    // Watchers
    watch(() => props.show, (newVal) => {
      if (newVal) {
        loadConversations()
        nextTick(() => {
          if (inputField.value) {
            inputField.value.focus()
          }
        })
      }
    })

    // Lifecycle
    onMounted(() => {
      if (props.show) {
        loadConversations()
      }
    })

    return {
      messages,
      inputMessage,
      isTyping,
      conversations,
      currentConversationId,
      showHistory,
      messagesContainer,
      inputField,
      isCoach,
      suggestions,
      showTrainingPanel,
      students,
      isGenerating,
      isSaving,
      generatedTraining,
      trainingForm,
      percentOptions,
      loadZonesPreview,
      closeChat,
      sendMessage,
      sendSuggestion,
      loadConversation,
      deleteConversation,
      startNewConversation,
      formatMessage,
      formatTime,
      formatDate,
      translateDay,
      getWeeksCount,
      openTrainingPanel,
      generateTraining,
      saveTraining
    }
  }
}
</script>

<style scoped>
.ai-chat-container {
  position: fixed;
  bottom: 90px;
  right: 20px;
  z-index: 1050;
}

.ai-chat-window {
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 550px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-chat-header {
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-avatar {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.ai-avatar-large {
  width: 60px;
  height: 60px;
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  margin: 0 auto;
}

.ai-history-sidebar {
  position: absolute;
  top: 60px;
  left: 0;
  width: 250px;
  height: calc(100% - 120px);
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  z-index: 10;
  overflow-y: auto;
}

.ai-history-list {
  padding: 10px;
}

.ai-history-item {
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 5px;
  transition: background 0.2s;
}

.ai-history-item:hover {
  background: #e9ecef;
}

.ai-history-item.active {
  background: #e9ecef;
  border-left: 3px solid #7928CA;
}

.ai-training-panel {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex: 1;
  overflow-y: auto;
}

.training-info {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.training-info .badge {
  white-space: normal;
  word-break: break-word;
  text-align: left;
  max-width: 100%;
}

.training-weeks-preview {
  max-height: 300px;
  overflow-y: auto;
  font-size: 12px;
}

.week-item {
  padding: 8px;
  background: #fff;
  border-radius: 6px;
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.ai-welcome-message {
  text-align: center;
  padding: 20px;
}

.quick-actions {
  margin-bottom: 15px;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.suggestion-chip {
  padding: 8px 14px;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.suggestion-chip:hover {
  background: #7928CA;
  color: #fff;
  border-color: #7928CA;
}

.ai-message {
  display: flex;
  margin-bottom: 15px;
  gap: 10px;
}

.ai-message.user {
  flex-direction: row-reverse;
}

.ai-message .message-avatar {
  width: 32px;
  height: 32px;
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  flex-shrink: 0;
}

.ai-message.user .message-avatar {
  background: linear-gradient(310deg, #2152ff 0%, #21d4fd 100%);
}

.message-content {
  max-width: 75%;
}

.message-text {
  background: #fff;
  padding: 12px 16px;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  font-size: 14px;
  line-height: 1.5;
}

.ai-message.user .message-text {
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  color: #fff;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 4px;
}

.message-time {
  display: block;
  margin-top: 4px;
  color: #adb5bd;
  font-size: 11px;
}

.ai-message.user .message-time {
  text-align: right;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #adb5bd;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-8px);
  }
}

.generating-indicator {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(310deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  font-size: 14px;
  color: #7928CA;
  animation: pulse-bg 2s infinite ease-in-out;
}

.generating-indicator i {
  font-size: 16px;
}

@keyframes pulse-bg {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.ai-chat-input {
  padding: 15px;
  background: #fff;
  border-top: 1px solid #e9ecef;
}

.input-wrapper {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.input-wrapper textarea {
  flex: 1;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  padding: 10px 16px;
  resize: none;
  font-size: 14px;
  max-height: 100px;
  line-height: 1.4;
}

.input-wrapper textarea:focus {
  outline: none;
  border-color: #7928CA;
}

.send-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(310deg, #7928CA 0%, #FF0080 100%);
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

/* Message text formatting */
.message-text :deep(p) {
  margin-bottom: 8px;
}

.message-text :deep(p:last-child) {
  margin-bottom: 0;
}

.message-text :deep(ul),
.message-text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.message-text :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.message-text :deep(pre) {
  background: rgba(0, 0, 0, 0.05);
  padding: 10px;
  border-radius: 8px;
  overflow-x: auto;
}

/* Responsive */
@media (max-width: 480px) {
  .ai-chat-container {
    bottom: 80px;
    right: 10px;
    left: 10px;
  }

  .ai-chat-window {
    width: 100%;
    height: calc(100vh - 100px);
  }
}
</style>
